import React, { FC, useContext } from "react"
import { Col, Row } from "react-bootstrap"
import { useSelector } from "react-redux"
import _ from "lodash"

import FormToolkit from "@/form_toolkit"
import {
  getActiveBid,
  getFeatures,
  getForm,
  getProduct,
  isProductReady,
} from "@/onboarding/features/product/selectors"
import { filter } from "@/onboarding/features/product/Slice"
import { ReservedFields, TenderStatuses } from "@/onboarding/features/product/models/Product"
import LoadingSpinner from "@/components/LoadingSpinner"
import AuthDescriptorContext from "@/auth_descriptor/Context"
import { isBlank } from "@/utils"

import CruiseLineOnly from "@/auth_descriptor/CruiseLineOnly"
import SeasonDropdown from "./header/SeasonDropdown"
import TourStatus from "./header/TourStatus"
import BidStatus from "./header/BidStatus"
import ToolsDropdown from "./header/ToolsDropdown"
import ToggleFormsButton from "./header/ToggleFormsButton"
import ToggleExpandButton from "./header/ToggleExpandButton"
import ToggleProposedDetailsButton from "./header/ToggleProposedDetailsButton"
import ConfirmContractPeriodDetailsButton from "./header/ConfirmContractPeriodDetailsButton"
import RejectContractPeriodDetailsButton from "./header/RejectContractPeriodDetailsButton"
import AcceptProposedChangesButton from "./header/AcceptProposedChangesButton"
import RejectProposedChangesButton from "./header/RejectProposedChangesButton"
import DetailsConfirmationStatus from "./header/DetailsConfirmationStatus"
import { InlineDisplayValue } from "./shared/Field"
import TextDisplay from "./shared/TextDisplay"

const Select = FormToolkit.buildReduxSingleSelect(getForm, filter)

const DetailRow = ({ label, children }) => (
  <div>
    <span style={{ fontWeight: "bold" }}>{label}:</span>
    <span className="d-inline ms-2" style={{ display: "block" }}>
      {children}
    </span>
  </div>
)

const Header: FC = () => {
  const product = useSelector(getProduct)
  const productReady = useSelector(isProductReady)
  const features = useSelector(getFeatures)
  const activeBid = useSelector(getActiveBid)

  const authDescriptor = useContext(AuthDescriptorContext)
  const showIncumbentOperator =
    product.incumbentCompany &&
    (authDescriptor.isCruiseLineUi || authDescriptor.companyId === product.incumbentCompany?.id)
  const showAwardedOperator =
    authDescriptor.isCruiseLineUi || authDescriptor.companyId === product.awardedCompany?.id
  const showTourStatus = showAwardedOperator && !isBlank(product.awardedCompany)
  const productCancelled = product.tenderStatus === TenderStatuses.CANCELLED
  const showCancellationReason = showTourStatus && productCancelled
  const displayedCancellationReason = isBlank(product.cancellationReason)
    ? "No Reason Given"
    : product.cancellationReason

  const MainDetailsDisplay = () => (
    <div className="mb-3">
      {showIncumbentOperator && (
        <DetailRow label="Incumbent Operator">
          {authDescriptor.isCruiseLineUi && product.incumbentCompany.operatorProfileId ? (
            <a
              href={`/onboarding/operator_profiles/${product.incumbentCompany.operatorProfileId}`}
              className="text-success"
            >
              {product.incumbentCompany.name}
            </a>
          ) : (
            product.incumbentCompany.name
          )}
        </DetailRow>
      )}
      <DetailRow label="Owner">{product.managingCompany.name}</DetailRow>
      {showTourStatus && (
        <DetailRow label="Tour Status">
          <TourStatus />
        </DetailRow>
      )}
      {showCancellationReason && (
        <DetailRow label="Cancellation Reason">
          <>{displayedCancellationReason}</>
        </DetailRow>
      )}
      {activeBid && (
        <DetailRow label="Bid Status">
          <BidStatus />
        </DetailRow>
      )}
      <DetailRow label="Port">{`${product.port.name} (${product.port.countryName})`}</DetailRow>
      {product.biddingRegion && (
        <DetailRow label="Bidding Region">{product.biddingRegion.name}</DetailRow>
      )}
      <CruiseLineOnly>
        <DetailRow label="Tour Type">
          <InlineDisplayValue name={ReservedFields.TOUR_TYPE} />
        </DetailRow>
        <DetailRow label="Tour Language">
          <InlineDisplayValue name={ReservedFields.TOUR_LANGUAGE} />
        </DetailRow>
        <DetailRow label="Internal Tour Status">
          <TextDisplay value={product.internalTag?.name} />
        </DetailRow>
      </CruiseLineOnly>
      {!_.isEmpty(product.cruiseBrands) && (
        <DetailRow label="Cruise Brands">
          {product.cruiseBrands.map((brand) => brand.code).join(", ")}
        </DetailRow>
      )}
      {product.detailsConfirmation && (
        <DetailRow label="Tour Details">
          <DetailsConfirmationStatus />
        </DetailRow>
      )}
      {product.otalioStatus && (
        <DetailRow label="Otalio Auto-Export">
          {product.otalioStatus.autoRun ? "Enabled" : "Disabled"}
          {product.otalioStatus.lastRunAt && (
            <> &middot; Last exported {product.otalioStatus.lastRunAt}</>
          )}
        </DetailRow>
      )}
    </div>
  )

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex justify-content-between gap-2">
        <div>
          <h1 className="h4 mb-0 d-flex gap-2">
            {`${product.name}: ${_.join(product.codes, ", ")}`}
            {!productReady && <LoadingSpinner />}
          </h1>
        </div>
        <div className="d-flex flex-wrap justify-content-end gap-2">
          <ToggleExpandButton />
          <ToggleFormsButton />
          <ToggleProposedDetailsButton />
          <ConfirmContractPeriodDetailsButton />
          <RejectContractPeriodDetailsButton />
          <AcceptProposedChangesButton />
          <RejectProposedChangesButton />
          <ToolsDropdown />
        </div>
      </div>
      <MainDetailsDisplay />
      {(features.general.product.showSeasonsFilter ||
        features.general.product.showRatePeriods ||
        features.general.product.toggleBids) && (
        <Row>
          {features.general.product.showSeasonsFilter && (
            <Col md={2}>
              <SeasonDropdown />
            </Col>
          )}
          {features.general.product.showRatePeriods && (
            <Col md={2}>
              <Select name="contractPeriodId" label="Rate Periods" />
            </Col>
          )}
          {features.general.product.toggleBids && (
            <Col md={2}>
              <Select name="bidId" />
            </Col>
          )}
        </Row>
      )}
    </div>
  )
}

export default Header
